import { useMemo, useEffect, useRef } from 'react'

import { isKubernetesAttackActive } from 'components/attacks/utils/kubernetes'
import useAgent from 'hooks/use-agent'
import useAuth from 'hooks/use-auth'
import { hasTruthy } from 'utils/list'

function getAttackStart({ createdAt, start_time }) {
  return new Date(start_time || createdAt)
}

function sortByAttackStart(a, b) {
  return getAttackStart(b) - getAttackStart(a)
}

export function createKubernetesAttackStateMap(kubernetesFetchState) {
  if (kubernetesFetchState.hasError) {
    return { active: [], completed: [] }
  }

  return kubernetesFetchState.data.reduce(
    (k8AttackStateMap, k8Attack) => {
      if (k8Attack.attackId) {
        // If we have an attackId we can use the infra attacks stage
        k8AttackStateMap[k8Attack.attackId] = k8Attack
      } else if (isKubernetesAttackActive(k8Attack)) {
        // K8 Attack is resolving but hasn't errored so its "active"
        k8AttackStateMap.active.push(k8Attack)
      } else {
        // K8 attack has no longer active
        k8AttackStateMap.completed.push(k8Attack)
      }

      return k8AttackStateMap
    },
    { active: [], completed: [] }
  )
}

export default function useAttackResource({
  active: fetchActive = true,
  completed: fetchCompleted = true,
  kubernetes: fetchKubernetes = true,
  scenarios: fetchScenarios = true,
  application = true,
  interval = 10000,
  shouldCache = true,
  fetchCompletedFully = true,
  enabled = true
}) {
  const { currentTeamId, companyIsAlfiEnabled, isPaywallActive } = useAuth()
  const fetchApplication = companyIsAlfiEnabled && application
  const activeAttackCount = useRef(0)

  const isEnabled = enabled && !isPaywallActive

  const {
    data: [activeApplicationAttacks, completedApplicationAttacks],
    doFetch: doFetchApplicationAttacks,
    isLoading: isLoadingApplicationAttacks
  } = useAgent(
    [
      `/experiments/active?teamId=${currentTeamId}`,
      `/experiments/completed?teamId=${currentTeamId}`
    ],
    [[], []],
    {
      skipRequest: !isEnabled || !fetchApplication,
      shouldCache,
      interval
    }
  )

  const {
    data: activeScenarios,
    doFetch: doFetchActiveScenarios,
    isLoading: activeScenariosLoading
  } = useAgent(
    `/scenarios/active?teamId=${currentTeamId}`,

    [],
    {
      skipRequest: !isEnabled || !fetchScenarios,
      shouldCache,
      interval
    }
  )

  const kubernetesFetchState = useAgent(
    `/kubernetes/attacks?teamId=${currentTeamId}`,
    [],
    {
      shouldCache,
      skipRequest: !isEnabled || !fetchKubernetes,
      interval,
      ignoreStatusCode: 404
    }
  )

  const activeFetchState = useAgent(
    `/attacks/active?teamId=${currentTeamId}`,
    [],
    {
      skipRequest: !isEnabled || !fetchActive,
      shouldCache,
      interval
    }
  )

  const partialCompletedFetchState = useAgent(
    `/attacks/completed?pageSize=100&teamId=${currentTeamId}`,
    [],
    {
      skipRequest: !isEnabled || !fetchCompleted,
      shouldCache
    }
  )

  const partialCompletedAttacksLoaded =
    partialCompletedFetchState.fetchSuccessCount > 0

  const fullCompletedFetchState = useAgent(
    `/attacks/completed?teamId=${currentTeamId}`,
    partialCompletedFetchState.data, // Preload full request with partial request data
    {
      shouldCache,
      skipRequest:
        !isEnabled ||
        !partialCompletedAttacksLoaded ||
        !fetchCompleted ||
        !fetchCompletedFully
    }
  )

  const fullCompletedAttacksLoaded =
    fullCompletedFetchState.fetchSuccessCount > 0

  useEffect(() => {
    if (activeAttackCount.current !== activeFetchState.data.length) {
      activeAttackCount.current = activeFetchState.data.length

      if (partialCompletedAttacksLoaded) {
        fullCompletedFetchState.doFetch()
      }
    }
  }, [
    activeAttackCount,
    activeFetchState,
    fullCompletedFetchState,
    partialCompletedAttacksLoaded
  ])

  const completedAttackFetchState = fullCompletedAttacksLoaded
    ? fullCompletedFetchState
    : partialCompletedFetchState

  const kubernetesAttackStateMap = useMemo(
    () => createKubernetesAttackStateMap(kubernetesFetchState),
    [kubernetesFetchState]
  )

  const combinedActive = useMemo(() => {
    return activeFetchState.data
      .map((infraAttack) => {
        if (kubernetesAttackStateMap[infraAttack.guid]) {
          return {
            ...kubernetesAttackStateMap[infraAttack.guid],
            infraAttack: infraAttack
          }
        }
        return infraAttack
      })
      .concat(kubernetesAttackStateMap.active)
      .sort(sortByAttackStart)
  }, [activeFetchState.data, kubernetesAttackStateMap])

  const combinedCompleted = useMemo(
    () =>
      completedAttackFetchState.data
        .map((infraAttack) => {
          if (kubernetesAttackStateMap[infraAttack.guid]) {
            return {
              ...kubernetesAttackStateMap[infraAttack.guid],
              infraAttack: infraAttack
            }
          }
          return infraAttack
        })
        .concat(kubernetesAttackStateMap.completed)
        .sort(sortByAttackStart),
    [completedAttackFetchState.data, kubernetesAttackStateMap]
  )

  const activeAttacksCount = useMemo(
    () =>
      combinedActive.length +
      activeScenarios.length +
      activeApplicationAttacks.length,
    [combinedActive, activeScenarios, activeApplicationAttacks]
  )

  const returnState = useMemo(() => {
    return {
      states: {
        kubernetes: kubernetesFetchState,
        completed: partialCompletedFetchState
      },
      aggregates: {
        completedAttacks: combinedCompleted,
        activeAttacks: combinedActive,
        activeCount: activeAttacksCount
      },
      completed: combinedCompleted,
      active: combinedActive,
      activeAttacksCount: activeAttacksCount,
      activeScenarios,
      activeScenariosLoading,
      activeApplicationAttacks,
      completedApplicationAttacks,
      doFetchActiveScenarios,
      doFetchApplicationAttacks,
      partialCompletedAttacksLoaded,
      kubernetesAttacksLoading: kubernetesFetchState.isLoading,
      applicationAttacksLoading: isLoadingApplicationAttacks,
      isLoading: hasTruthy(
        activeFetchState.isLoading,
        completedAttackFetchState.isLoading,
        kubernetesFetchState.isLoading
      ),
      hasError: hasTruthy(
        activeFetchState.hasError,
        completedAttackFetchState.hasError,
        kubernetesFetchState.hasError
      ),
      doFetch: () => {
        if (fetchKubernetes) {
          kubernetesFetchState.doFetch()
        }
        if (fetchActive) {
          activeFetchState.doFetch()
        }
        if (fetchCompleted) {
          fullCompletedFetchState.doFetch()
        }
      }
    }
  }, [
    kubernetesFetchState,
    partialCompletedFetchState,
    combinedCompleted,
    combinedActive,
    activeAttacksCount,
    activeScenarios,
    activeScenariosLoading,
    activeApplicationAttacks,
    completedApplicationAttacks,
    doFetchActiveScenarios,
    doFetchApplicationAttacks,
    partialCompletedAttacksLoaded,
    isLoadingApplicationAttacks,
    activeFetchState,
    completedAttackFetchState.isLoading,
    completedAttackFetchState.hasError,
    fetchKubernetes,
    fetchActive,
    fetchCompleted,
    fullCompletedFetchState
  ])

  return returnState
}
